import {
  ActionIcon, Badge, Button, Group, Modal, NumberInput, Paper, Select, Stack, Table, Text, Textarea, TextInput, Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, unwrap } from "../../api/client";
import type { components } from "../../api/schema";
import { useT } from "../../i18n/script";
import AdminLayout from "./AdminLayout";

type Rule = components["schemas"]["DataRule"];
const ACCESS_COLOR = { ALLOW: "green", DENY: "red", LIMIT: "orange" } as const;
const SERVICES = ["*", "WMS", "WFS", "WCS", "WMTS", "WPS", "CSW", "GWC", "OGCAPI-FEATURES"];

export default function DataRulesPage() {
  const { t } = useT();
  const qc = useQueryClient();
  const rules = useQuery({ queryKey: ["rules"], queryFn: async () => unwrap(await api.GET("/api/v1/admin/data-rules")) });
  const roles = useQuery({ queryKey: ["admin-roles"], queryFn: async () => unwrap(await api.GET("/api/v1/admin/roles")) });
  const [edit, setEdit] = useState<Rule | null>(null);

  async function save(r: Rule) {
    try {
      const body = { ...r, service: r.service === "*" ? null : r.service };
      if (r.id) unwrap(await api.PUT("/api/v1/admin/data-rules/{rule_id}", { params: { path: { rule_id: r.id } }, body }));
      else unwrap(await api.POST("/api/v1/admin/data-rules", { body }));
      setEdit(null);
      qc.invalidateQueries({ queryKey: ["rules"] });
      notifications.show({ color: "green", message: t("Pravilo sačuvano u GeoFence") });
    } catch (e) { notifications.show({ color: "red", message: String(e) }); }
  }
  async function remove(id: number) {
    if (!window.confirm(t("Obrisati pravilo?"))) return;
    try {
      unwrap(await api.DELETE("/api/v1/admin/data-rules/{rule_id}", { params: { path: { rule_id: id } } }));
      qc.invalidateQueries({ queryKey: ["rules"] });
    } catch (e) { notifications.show({ color: "red", message: String(e) }); }
  }

  const roleOpts = [{ value: "", label: t("(svi)") }, ...(roles.data ?? []).map((r) => ({ value: r.name, label: r.name })),
    { value: "ROLE_ANONYMOUS", label: "ROLE_ANONYMOUS (neprijavljeni)" }];

  return (
    <AdminLayout>
      <Stack>
        <Group justify="space-between">
          <div>
            <Title order={3}>{t("Pristup podacima (GeoFence)")}</Title>
            <Text size="sm" c="dimmed">{t("Pravila za OGC servise (WMS/WFS/WFS-T/WCS...) po roli, sloju i prostornoj zoni. Primjenjuje se prvo pravilo sa najmanjim prioritetom.")}</Text>
          </div>
          <Button leftSection={<IconPlus size={16} />} onClick={() => setEdit({ priority: 100, workspace: "agrisense", access: "ALLOW",
            allowed_area_srid: 8678, spatial_filter: "INTERSECT", catalog_mode: "HIDE" } as Rule)}>{t("Novo pravilo")}</Button>
        </Group>
        <Paper withBorder>
          <Table striped verticalSpacing="xs">
            <Table.Thead><Table.Tr>
              <Table.Th>{t("Prioritet")}</Table.Th><Table.Th>{t("Rola")}</Table.Th><Table.Th>Workspace</Table.Th>
              <Table.Th>{t("Sloj")}</Table.Th><Table.Th>{t("Servis")}</Table.Th><Table.Th>{t("Zahtjev")}</Table.Th>
              <Table.Th>{t("Pristup")}</Table.Th><Table.Th>{t("Zona")}</Table.Th><Table.Th w={80} />
            </Table.Tr></Table.Thead>
            <Table.Tbody>
              {(rules.data ?? []).map((r) => (
                <Table.Tr key={r.id}>
                  <Table.Td>{r.priority}</Table.Td>
                  <Table.Td translate="no">{r.role ?? <Text c="dimmed" size="sm">*</Text>}</Table.Td>
                  <Table.Td translate="no">{r.workspace ?? "*"}</Table.Td>
                  <Table.Td translate="no">{r.layer ?? "*"}</Table.Td>
                  <Table.Td translate="no">{r.service ?? "*"}</Table.Td>
                  <Table.Td translate="no">{r.request ?? "*"}</Table.Td>
                  <Table.Td><Badge color={ACCESS_COLOR[r.access ?? "ALLOW"]}>{r.access}</Badge></Table.Td>
                  <Table.Td>{r.allowed_area_wkt ? <Badge variant="outline" color="orange">{t("ograničena")}</Badge> : "—"}</Table.Td>
                  <Table.Td>
                    <Group gap={2}>
                      <ActionIcon variant="subtle" onClick={() => setEdit({ ...r, service: r.service ?? "*" })}><IconEdit size={16} /></ActionIcon>
                      <ActionIcon variant="subtle" color="red" onClick={() => remove(r.id!)}><IconTrash size={16} /></ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
        <Modal opened={!!edit} onClose={() => setEdit(null)} size="lg" title={edit?.id ? `${t("Pravilo")} #${edit.id}` : t("Novo pravilo")}>
          {edit && (
            <Stack>
              <Group grow>
                <NumberInput label={t("Prioritet")} value={edit.priority ?? 100} onChange={(v) => setEdit({ ...edit, priority: Number(v) })} />
                <Select label={t("Pristup")} data={["ALLOW", "DENY", "LIMIT"]} value={edit.access}
                  onChange={(v) => setEdit({ ...edit, access: (v ?? "ALLOW") as Rule["access"] })} />
              </Group>
              <Group grow>
                <Select label={t("Rola")} data={roleOpts} value={edit.role ?? ""} onChange={(v) => setEdit({ ...edit, role: v || null })} />
                <TextInput label={t("Korisnik (opciono)")} value={edit.username ?? ""} onChange={(e) => setEdit({ ...edit, username: e.currentTarget.value || null })} />
              </Group>
              <Group grow>
                <TextInput label="Workspace" value={edit.workspace ?? ""} onChange={(e) => setEdit({ ...edit, workspace: e.currentTarget.value || null })} />
                <TextInput label={t("Sloj")} placeholder="*" value={edit.layer ?? ""} onChange={(e) => setEdit({ ...edit, layer: e.currentTarget.value || null })} />
              </Group>
              <Group grow>
                <Select label={t("Servis")} data={SERVICES} value={(edit.service as string) ?? "*"} onChange={(v) => setEdit({ ...edit, service: v as Rule["service"] })} />
                <TextInput label={t("Zahtjev")} placeholder="GETMAP, GETFEATURE, TRANSACTION" value={edit.request ?? ""}
                  onChange={(e) => setEdit({ ...edit, request: e.currentTarget.value.toUpperCase() || null })} />
              </Group>
              {edit.access === "LIMIT" && (
                <>
                  <Textarea label={t("Dozvoljena zona (WKT MULTIPOLYGON)")} autosize minRows={3} value={edit.allowed_area_wkt ?? ""}
                    onChange={(e) => setEdit({ ...edit, allowed_area_wkt: e.currentTarget.value || null })} />
                  <Group grow>
                    <NumberInput label="SRID" value={edit.allowed_area_srid ?? 8678} onChange={(v) => setEdit({ ...edit, allowed_area_srid: Number(v) })} />
                    <Select label={t("Prostorni filter")} data={["INTERSECT", "CLIP"]} value={edit.spatial_filter}
                      onChange={(v) => setEdit({ ...edit, spatial_filter: (v ?? "INTERSECT") as Rule["spatial_filter"] })} />
                    <Select label={t("Katalog")} data={["HIDE", "CHALLENGE", "MIXED"]} value={edit.catalog_mode ?? "HIDE"}
                      onChange={(v) => setEdit({ ...edit, catalog_mode: v as Rule["catalog_mode"] })} />
                  </Group>
                </>
              )}
              <Button onClick={() => save(edit)}>{t("Sačuvaj")}</Button>
            </Stack>
          )}
        </Modal>
      </Stack>
    </AdminLayout>
  );
}
